import {
  GetCompanySearchResponse,
  PostApplicationRequestBody,
} from '@iwoca/lapi-client/edge';
import { currencyToNumber } from '@iwoca/orion';

import { TGetStartedDataDE } from './GetStartedDE/GetStartedDE';
import { TGetStartedDataUK } from './GetStartedUK/GetStartedUK';
import { getInitialStateBody } from './GetStartedUK/GetStartedUK.helpers';
import { AuthApiError, getAuthMe, postAuthSignup } from '../../api/authApi';
import {
  fetchPutAndGetState,
  usePostApplicationMutation,
  usePostSignupMutation,
  usePutApplicationMutation,
} from '../../api/lendingApi/lendingApiMutations';
import {
  useGetApplicationQuery,
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../api/lendingApi/lendingApiQueries';
import { getUpdatedApplicationBody } from '../../helpers/getApplicationBody';
import { useContextStateKey } from '../../hooks/useContextStateKey';
import { useLocale } from '../../hooks/useLocale';
import { useSignupParams } from '../../hooks/useSignupParams';
import { DATA_LAYER_EVENT, postToDataLayer } from '../../utils/googleDataLayer';
import {
  denormaliseGetStartedData,
  normaliseStepData,
} from '../helpers/stateNormalisation';
import { GET_STARTED_STEP_ID } from '../helpers/steps';

// Thrown by the Cognito-first orchestration when auth-service reports the
// email is already in the Cognito pool. Caller shows the duplicate-email UX
// (matching the on-blur LAPI email-availability check) instead of the generic
// submission-failed error.
export class DuplicateEmailError extends Error {
  email: string;

  constructor(email: string) {
    super(`Email ${email} is already registered in Cognito`);
    this.name = 'DuplicateEmailError';
    this.email = email;
  }
}

// 409 can be a genuine duplicate OR a retry after a mid-flow failure where
// auth-service already minted the user in this session (postAuthSignup
// succeeded, the follow-up /signup blipped, user retried). Probe /auth/me: if
// the current session owns this email, backend /signup is idempotent so
// continue. Otherwise this is a true duplicate and blocks signup.
export const mintCognitoUserForSignup = async (email: string) => {
  try {
    await postAuthSignup({ email });
  } catch (err) {
    if (!(err instanceof AuthApiError) || err.status !== 409) throw err;

    const me = await getAuthMe();
    const normalise = (s: string) => s.trim().toLowerCase();
    if (!me || normalise(me.email) !== normalise(email)) {
      throw new DuplicateEmailError(email);
    }
  }
};

export const useSubmitGetStarted = () => {
  const { locale } = useLocale();
  const { state } = useGetStateQuery();
  const { application } = useGetApplicationQuery();

  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepData(data)[GET_STARTED_STEP_ID],
  );

  const { affiliateTag, fromCampaign } = useSignupParams();
  const stateKeyFromContext = useContextStateKey();

  const { mutatePostSignup } = usePostSignupMutation();
  const { mutatePostApplication } = usePostApplicationMutation();
  const { mutatePutApplication } = usePutApplicationMutation();

  const submitGetStarted = async ({
    applicationBody,
    hadCompleteApplication,
    values,
  }: {
    applicationBody: PostApplicationRequestBody;
    hadCompleteApplication: boolean;
    values:
      | TGetStartedDataDE
      | (TGetStartedDataUK & {
          prefilledCompany?: GetCompanySearchResponse['data'][number] | null;
        });
  }) => {
    let stateKeyForApplication: string | null | undefined = stateKeyFromContext;

    const stateBody = getInitialStateBody(affiliateTag, fromCampaign, {
      ...values,
    });

    // Submit new application and state
    if (!application && !normalisedState) {
      // Cognito-first two-call orchestration:
      // 1. auth-service /signup mints the Cognito user + iwoca_session cookie
      //    (returns 409 if the email is already registered in the Cognito pool)
      // 2. envoy validates the cookie on the follow-up state POST and injects
      //    iwoca-user-id, which iwoca-django reads at signup time.
      await mintCognitoUserForSignup(values.email);

      stateKeyForApplication = await mutatePostSignup({
        body: stateBody,
      });

      if (stateKeyForApplication) {
        await mutatePostApplication({
          stateKey: stateKeyForApplication,
          body: applicationBody,
        });
      }
    }
    // Submit new application, update state if state exists without email
    else if (!application && !normalisedState.email && stateKeyFromContext) {
      const statePutBody = denormaliseGetStartedData(state, values);
      await fetchPutAndGetState({
        body: statePutBody,
        stateKey: stateKeyFromContext,
      });

      await mutatePostApplication({
        body: applicationBody,
        stateKey: stateKeyFromContext,
      });
    }
    // Submit new application if state exists with email
    else if (!application && Boolean(normalisedState) && stateKeyFromContext) {
      await mutatePostApplication({
        stateKey: stateKeyFromContext,
        body: applicationBody,
      });
    }
    // A deferred application is closed, so create a new one.
    else if (application?.status === 'deferred' && stateKeyFromContext) {
      if (!normalisedState?.email) {
        const statePutBody = denormaliseGetStartedData(state, values);
        await fetchPutAndGetState({
          body: statePutBody,
          stateKey: stateKeyFromContext,
        });
      }

      await mutatePostApplication({
        stateKey: stateKeyFromContext,
        body: applicationBody,
      });
    }
    // Update state and/or application if they exists but email or amount was missing
    else if (
      Boolean(application) &&
      (!normalisedState.email || !hadCompleteApplication) &&
      stateKeyFromContext
    ) {
      if (!normalisedState.email) {
        const statePutBody = denormaliseGetStartedData(state, values);
        await fetchPutAndGetState({
          body: statePutBody,
          stateKey: stateKeyFromContext,
        });
      }

      if (!hadCompleteApplication) {
        await mutatePutApplication({
          stateKey: stateKeyFromContext,
          body: getUpdatedApplicationBody(application, values, locale),
        });
      }
    }
  };

  /**
   * Add data layer request when submission succeeds.
   * Set applicationData to match current webflow implementation.
   */
  const postGoogleTrackingSuccess = (
    values: TGetStartedDataUK | TGetStartedDataDE,
  ) => {
    const applicationData = {
      event: DATA_LAYER_EVENT,
      applicationData: {
        applicantEmailAddress: values.email,
        requestedAmount: values.amount
          ? currencyToNumber(values.amount, {
              currencySymbol: true,
              locale: locale === 'de' ? 'de-DE' : locale,
              minorUnits: false,
            })
          : undefined,
        email_marketing_opt_in: !!values.marketingOptIn,
        lengthOfLoan: values.loanDuration,
        purpose: values.loanPurpose,
        ...(locale === 'de'
          ? { detailed_purpose: values.loanPurposeDetails }
          : {}),
      },
    };

    postToDataLayer(applicationData);
  };

  return {
    submitGetStarted,
    postGoogleTrackingSuccess,
  };
};
